/**
 * ============================================================================
 * CREATE TRANSACTION MODAL v3.3 - BUTTON STRATEGY ALIGNMENT
 * ============================================================================
 *
 * ✅ v3.3: BUTTON STRATEGY ALIGNMENT
 *          - btnPrimary: height:52px, radius:14px (L1 Primary)
 *          - btnSecondary/btnDanger: height:44px, radius:12px (L2)
 *          - closeBtn: 32px + ::before 44px Touch (L4 Mini)
 * ✅ v3.2: Durchsuchbares Kategorie-Dropdown
 * ✅ v3.1: FIX - Kategorien Deduplizierung
 * ✅ v3.0: FIX - Kategorien is_fixed Gruppierung
 * ✅ v2.0: Planivio Rebranding
 * ✅ v1.0: Original Modal
 *
 * @version 3.3.0 - Button Strategy Alignment
 */

import React, { useState, useEffect, useRef } from 'react';

// ============================================================================
// PLANIVIO STYLES — ✅ v3.3: Button Strategy Alignment
// ============================================================================
const styles = {
  overlay: {
    position: 'fixed',
    inset: 0,
    background: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
    padding: '1rem',
    backdropFilter: 'blur(4px)'
  },
  modal: {
    background: 'white',
    borderRadius: '16px',
    width: '100%',
    maxWidth: '480px',
    maxHeight: '90vh',
    overflow: 'auto',
    boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)'
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '1.25rem 1.5rem',
    borderBottom: '2px solid #B8D4DC',
    background: '#E8F4F8'
  },
  title: {
    margin: 0,
    fontSize: '1.1rem',
    fontWeight: 700,
    color: '#1F2937',
    display: 'flex',
    alignItems: 'center',
    gap: '8px'
  },
  // ✅ v3.3: L4 Mini (32px visuell, Touch via className)
  closeBtn: {
    width: '32px',
    height: '32px',
    border: 'none',
    background: 'transparent',
    fontSize: '1.5rem',
    cursor: 'pointer',
    color: '#6B7280',
    borderRadius: '8px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'all 0.2s',
    position: 'relative',
    padding: 0,
    lineHeight: 1,
    boxSizing: 'border-box'
  },
  body: {
    padding: '1.5rem'
  },
  formGroup: {
    marginBottom: '1rem'
  },
  formRow: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '1rem'
  },
  label: {
    display: 'block',
    marginBottom: '6px',
    fontWeight: 600,
    fontSize: '0.9rem',
    color: '#374151'
  },
  input: {
    width: '100%',
    padding: '0.75rem 1rem',
    border: '2px solid #B8D4DC',
    borderRadius: '10px',
    fontSize: '1rem',
    color: '#1F2937',
    background: '#F9FAFB',
    outline: 'none',
    transition: 'border-color 0.2s',
    boxSizing: 'border-box'
  },
  select: {
    width: '100%',
    padding: '0.75rem 1rem',
    border: '2px solid #B8D4DC',
    borderRadius: '10px',
    fontSize: '1rem',
    color: '#1F2937',
    background: '#F9FAFB',
    outline: 'none',
    cursor: 'pointer',
    transition: 'border-color 0.2s',
    boxSizing: 'border-box'
  },
  textarea: {
    width: '100%',
    padding: '0.75rem 1rem',
    border: '2px solid #B8D4DC',
    borderRadius: '10px',
    fontSize: '1rem',
    color: '#1F2937',
    background: '#F9FAFB',
    outline: 'none',
    resize: 'vertical',
    minHeight: '80px',
    fontFamily: 'inherit',
    transition: 'border-color 0.2s',
    boxSizing: 'border-box'
  },
  // Type Toggle Buttons (Segmented Control — nicht Button Strategy)
  typeToggle: {
    display: 'flex',
    gap: '0.5rem'
  },
  typeBtn: {
    flex: 1,
    padding: '0.75rem 1rem',
    border: '2px solid #B8D4DC',
    borderRadius: '10px',
    fontSize: '0.95rem',
    fontWeight: 600,
    cursor: 'pointer',
    transition: 'all 0.2s',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '6px'
  },
  typeBtnIncome: {
    background: '#F0FDF4',
    borderColor: '#86EFAC',
    color: '#22C55E'
  },
  typeBtnExpense: {
    background: '#FEF2F2',
    borderColor: '#FECACA',
    color: '#EF4444'
  },
  typeBtnActive: {
    transform: 'scale(1.02)',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)'
  },
  typeBtnInactive: {
    background: '#F9FAFB',
    borderColor: '#E5E7EB',
    color: '#9CA3AF'
  },
  actions: {
    display: 'flex',
    gap: '0.75rem',
    padding: '1rem 1.5rem',
    borderTop: '2px solid #B8D4DC',
    background: '#F9FAFB',
    alignItems: 'center'
  },
  // ✅ v3.3: L1 Primary (52px, 14px radius)
  btnPrimary: {
    flex: 1,
    height: '52px',
    padding: '0 28px',
    background: 'linear-gradient(135deg, #4A90A4 0%, #6BA893 100%)',
    color: 'white',
    border: 'none',
    borderRadius: '14px',
    fontSize: '0.95rem',
    fontWeight: 600,
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '6px',
    transition: 'all 0.2s',
    boxShadow: '0 4px 16px rgba(74, 144, 164, 0.25)',
    lineHeight: 1,
    boxSizing: 'border-box',
    whiteSpace: 'nowrap'
  },
  // ✅ v3.3: L2 Secondary (44px, 12px radius)
  btnSecondary: {
    height: '44px',
    padding: '0 20px',
    background: 'white',
    color: '#6B7280',
    border: '2px solid #B8D4DC',
    borderRadius: '12px',
    fontSize: '0.9rem',
    fontWeight: 600,
    cursor: 'pointer',
    transition: 'all 0.2s',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    lineHeight: 1,
    boxSizing: 'border-box',
    whiteSpace: 'nowrap'
  },
  // ✅ v3.3: L2 Danger variant (44px, 12px radius)
  btnDanger: {
    height: '44px',
    padding: '0 20px',
    background: '#FEF2F2',
    color: '#EF4444',
    border: '2px solid #FECACA',
    borderRadius: '12px',
    fontSize: '0.9rem',
    fontWeight: 600,
    cursor: 'pointer',
    marginRight: 'auto',
    transition: 'all 0.2s',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '6px',
    lineHeight: 1,
    boxSizing: 'border-box',
    whiteSpace: 'nowrap'
  },
  // Amount Input mit Währung
  amountWrapper: {
    position: 'relative'
  },
  amountSuffix: {
    position: 'absolute',
    right: '1rem',
    top: '50%',
    transform: 'translateY(-50%)',
    color: '#6B7280',
    fontWeight: 600,
    pointerEvents: 'none'
  },
  debugInfo: {
    padding: '0.5rem',
    background: '#FEF3C7',
    border: '1px solid #F59E0B',
    borderRadius: '8px',
    fontSize: '0.75rem',
    color: '#92400E',
    marginBottom: '1rem'
  },
  // Searchable Category Dropdown
  categoryDropdownWrapper: {
    position: 'relative'
  },
  categorySearchInput: {
    width: '100%',
    padding: '0.75rem 1rem',
    paddingRight: '2.5rem',
    border: '2px solid #B8D4DC',
    borderRadius: '10px',
    fontSize: '1rem',
    color: '#1F2937',
    background: '#F9FAFB',
    outline: 'none',
    transition: 'border-color 0.2s',
    boxSizing: 'border-box'
  },
  categorySearchIcon: {
    position: 'absolute',
    right: '1rem',
    top: '50%',
    transform: 'translateY(-50%)',
    color: '#6B7280',
    pointerEvents: 'none',
    fontSize: '1rem'
  },
  categoryDropdown: {
    position: 'absolute',
    top: '100%',
    left: 0,
    right: 0,
    maxHeight: '250px',
    overflowY: 'auto',
    background: 'white',
    border: '2px solid #4A90A4',
    borderTop: 'none',
    borderRadius: '0 0 10px 10px',
    zIndex: 100,
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
  },
  categoryGroup: {
    padding: '0.5rem 0'
  },
  categoryGroupLabel: {
    padding: '0.5rem 1rem',
    fontSize: '0.75rem',
    fontWeight: 700,
    color: '#6B7280',
    background: '#F3F4F6',
    textTransform: 'uppercase',
    letterSpacing: '0.5px'
  },
  categoryOption: {
    padding: '0.65rem 1rem',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '0.95rem',
    color: '#1F2937',
    transition: 'background 0.15s'
  },
  categoryOptionSelected: {
    background: '#E8F4F8',
    fontWeight: 600
  },
  categoryNoResults: {
    padding: '1rem',
    textAlign: 'center',
    color: '#6B7280',
    fontSize: '0.9rem'
  }
};

// ============================================================================
// COMPONENT
// ============================================================================
function CreateTransactionModal({ show, onClose, editing, categories, onSave, onDelete }) {
  
  const [type, setType] = useState(editing?.type || 'expense');
  const [amount, setAmount] = useState(editing?.amount || '');
  const [category, setCategory] = useState(editing?.category || '');
  const [description, setDescription] = useState(editing?.description || '');
  const [date, setDate] = useState(editing?.date || new Date().toISOString().split('T')[0]);
  const [notes, setNotes] = useState(editing?.notes || '');

  const [categorySearch, setCategorySearch] = useState('');
  const [showCategoryDropdown, setShowCategoryDropdown] = useState(false);
  const categoryDropdownRef = useRef(null);

  useEffect(() => {
    if (show) {
      setType(editing?.type || 'expense');
      setAmount(editing?.amount || '');
      setCategory(editing?.category || '');
      setDescription(editing?.description || '');
      setDate(editing?.date || new Date().toISOString().split('T')[0]);
      setNotes(editing?.notes || '');
      setCategorySearch('');
      setShowCategoryDropdown(false);
    }
  }, [show, editing]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (categoryDropdownRef.current && !categoryDropdownRef.current.contains(event.target)) {
        setShowCategoryDropdown(false);
      }
    };

    if (showCategoryDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showCategoryDropdown]);

  if (!show) return null;

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!amount || !category) {
      alert('Bitte fülle alle Pflichtfelder aus');
      return;
    }
    
    const data = {
      amount: parseFloat(amount),
      category,
      type,
      description,
      date,
      notes
    };
    
    console.log('💾 Saving transaction:', data);
    onSave(data);
  };

  const uniqueCategories = categories?.filter((cat, index, self) =>
    index === self.findIndex(c => c.id === cat.id)
  ) || [];

  const fixedCategories = uniqueCategories
    .filter(c => c.is_fixed === true)
    .sort((a, b) => a.name.localeCompare(b.name));
  
  const variableCategories = uniqueCategories
    .filter(c => c.is_fixed === false || c.is_fixed === undefined)
    .sort((a, b) => a.name.localeCompare(b.name));

  console.log('📋 CreateTransactionModal - Categories:', {
    raw: categories?.length || 0,
    unique: uniqueCategories.length,
    fixed: fixedCategories.length,
    variable: variableCategories.length,
    duplicatesRemoved: (categories?.length || 0) - uniqueCategories.length
  });

  return (
    <div style={styles.overlay} onClick={onClose}>
      <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
        
        {/* Header */}
        <div style={styles.header}>
          <h2 style={styles.title}>
            {editing ? '✏️ Transaktion bearbeiten' : '➕ Neue Transaktion'}
          </h2>
          <button 
            style={styles.closeBtn}
            className="tx-modal-close-btn"
            onClick={onClose}
            onMouseEnter={(e) => e.currentTarget.style.background = '#E5E7EB'}
            onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
          >
            ×
          </button>
        </div>

        {/* Body */}
        <form onSubmit={handleSubmit}>
          <div style={styles.body}>
            
            {(!uniqueCategories || uniqueCategories.length === 0) && (
              <div style={styles.debugInfo}>
                ⚠️ Keine Kategorien gefunden! Bitte zuerst Kategorien erstellen.
              </div>
            )}

            {/* Type Toggle (Segmented Control) */}
            <div style={styles.formGroup}>
              <label style={styles.label}>Typ *</label>
              <div style={styles.typeToggle}>
                <button
                  type="button"
                  onClick={() => setType('income')}
                  style={{
                    ...styles.typeBtn,
                    ...(type === 'income' 
                      ? { ...styles.typeBtnIncome, ...styles.typeBtnActive }
                      : styles.typeBtnInactive
                    )
                  }}
                >
                  💰 Einnahme
                </button>
                <button
                  type="button"
                  onClick={() => setType('expense')}
                  style={{
                    ...styles.typeBtn,
                    ...(type === 'expense' 
                      ? { ...styles.typeBtnExpense, ...styles.typeBtnActive }
                      : styles.typeBtnInactive
                    )
                  }}
                >
                  💸 Ausgabe
                </button>
              </div>
            </div>

            {/* Amount & Date Row */}
            <div style={styles.formRow}>
              <div style={styles.formGroup}>
                <label style={styles.label}>Betrag *</label>
                <div style={styles.amountWrapper}>
                  <input 
                    type="number"
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    required 
                    step="0.01"
                    min="0"
                    placeholder="0,00"
                    style={{ ...styles.input, paddingRight: '2.5rem' }}
                    onFocus={(e) => e.currentTarget.style.borderColor = '#4A90A4'}
                    onBlur={(e) => e.currentTarget.style.borderColor = '#B8D4DC'}
                  />
                  <span style={styles.amountSuffix}>€</span>
                </div>
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>Datum *</label>
                <input 
                  type="date"
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                  required
                  style={styles.input}
                  onFocus={(e) => e.currentTarget.style.borderColor = '#4A90A4'}
                  onBlur={(e) => e.currentTarget.style.borderColor = '#B8D4DC'}
                />
              </div>
            </div>

            {/* Searchable Category Dropdown */}
            <div style={styles.formGroup}>
              <label style={styles.label}>Kategorie * ({uniqueCategories.length} verfügbar)</label>
              <div style={styles.categoryDropdownWrapper} ref={categoryDropdownRef}>
                <input
                  type="text"
                  value={showCategoryDropdown ? categorySearch : (category || '')}
                  onChange={(e) => {
                    setCategorySearch(e.target.value);
                    if (!showCategoryDropdown) setShowCategoryDropdown(true);
                  }}
                  onFocus={() => {
                    setShowCategoryDropdown(true);
                    setCategorySearch('');
                  }}
                  placeholder={category ? category : "Kategorie suchen..."}
                  style={{
                    ...styles.categorySearchInput,
                    borderColor: showCategoryDropdown ? '#4A90A4' : '#B8D4DC',
                    borderRadius: showCategoryDropdown ? '10px 10px 0 0' : '10px'
                  }}
                />
                <span style={styles.categorySearchIcon}>🔍</span>

                {showCategoryDropdown && (
                  <div style={styles.categoryDropdown}>
                    {(() => {
                      const searchLower = categorySearch.toLowerCase();
                      const filteredFixed = fixedCategories.filter(c =>
                        c.name.toLowerCase().includes(searchLower)
                      );
                      const filteredVariable = variableCategories.filter(c =>
                        c.name.toLowerCase().includes(searchLower)
                      );
                      const hasResults = filteredFixed.length > 0 || filteredVariable.length > 0;

                      if (!hasResults) {
                        return (
                          <div style={styles.categoryNoResults}>
                            Keine Kategorie gefunden für "{categorySearch}"
                          </div>
                        );
                      }

                      return (
                        <>
                          {filteredFixed.length > 0 && (
                            <div style={styles.categoryGroup}>
                              <div style={styles.categoryGroupLabel}>📌 Fixkosten</div>
                              {filteredFixed.map(c => (
                                <div
                                  key={c.id}
                                  style={{
                                    ...styles.categoryOption,
                                    ...(category === c.name ? styles.categoryOptionSelected : {})
                                  }}
                                  onClick={() => {
                                    setCategory(c.name);
                                    setCategorySearch('');
                                    setShowCategoryDropdown(false);
                                  }}
                                  onMouseEnter={(e) => {
                                    if (category !== c.name) {
                                      e.currentTarget.style.background = '#F3F4F6';
                                    }
                                  }}
                                  onMouseLeave={(e) => {
                                    if (category !== c.name) {
                                      e.currentTarget.style.background = 'transparent';
                                    }
                                  }}
                                >
                                  {c.icon || '📁'} {c.name}
                                </div>
                              ))}
                            </div>
                          )}

                          {filteredVariable.length > 0 && (
                            <div style={styles.categoryGroup}>
                              <div style={styles.categoryGroupLabel}>🔄 Variable Kosten</div>
                              {filteredVariable.map(c => (
                                <div
                                  key={c.id}
                                  style={{
                                    ...styles.categoryOption,
                                    ...(category === c.name ? styles.categoryOptionSelected : {})
                                  }}
                                  onClick={() => {
                                    setCategory(c.name);
                                    setCategorySearch('');
                                    setShowCategoryDropdown(false);
                                  }}
                                  onMouseEnter={(e) => {
                                    if (category !== c.name) {
                                      e.currentTarget.style.background = '#F3F4F6';
                                    }
                                  }}
                                  onMouseLeave={(e) => {
                                    if (category !== c.name) {
                                      e.currentTarget.style.background = 'transparent';
                                    }
                                  }}
                                >
                                  {c.icon || '📁'} {c.name}
                                </div>
                              ))}
                            </div>
                          )}
                        </>
                      );
                    })()}
                  </div>
                )}

                <input
                  type="hidden"
                  value={category}
                  required
                />
              </div>
            </div>

            {/* Description */}
            <div style={styles.formGroup}>
              <label style={styles.label}>Beschreibung</label>
              <input 
                type="text"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="z.B. Wocheneinkauf REWE"
                style={styles.input}
                onFocus={(e) => e.currentTarget.style.borderColor = '#4A90A4'}
                onBlur={(e) => e.currentTarget.style.borderColor = '#B8D4DC'}
              />
            </div>

            {/* Notes */}
            <div style={styles.formGroup}>
              <label style={styles.label}>Notizen</label>
              <textarea 
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Optionale Notizen..."
                rows="2"
                style={styles.textarea}
                onFocus={(e) => e.currentTarget.style.borderColor = '#4A90A4'}
                onBlur={(e) => e.currentTarget.style.borderColor = '#B8D4DC'}
              />
            </div>

          </div>

          {/* Actions */}
          <div style={styles.actions}>
            {editing && onDelete && (
              <button 
                type="button"
                onClick={onDelete}
                style={styles.btnDanger}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = '#FEE2E2';
                  e.currentTarget.style.borderColor = '#EF4444';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = '#FEF2F2';
                  e.currentTarget.style.borderColor = '#FECACA';
                }}
              >
                🗑️ Löschen
              </button>
            )}
            
            <button 
              type="button"
              onClick={onClose}
              style={styles.btnSecondary}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = '#F3F4F6';
                e.currentTarget.style.borderColor = '#9CA3AF';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'white';
                e.currentTarget.style.borderColor = '#B8D4DC';
              }}
            >
              Abbrechen
            </button>
            
            <button 
              type="submit"
              style={styles.btnPrimary}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 6px 20px rgba(74, 144, 164, 0.4)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 4px 16px rgba(74, 144, 164, 0.25)';
              }}
            >
              💾 Speichern
            </button>
          </div>
        </form>

      </div>

      {/* ✅ v3.3: Touch-Target für Close Button (Inline-Styles können kein ::before) */}
      <style>{`
        .tx-modal-close-btn::before {
          content: '';
          position: absolute;
          inset: -6px;
        }
      `}</style>
    </div>
  );
}

export default CreateTransactionModal;